'use client';

import { type ChangeEvent, useRef, useState } from 'react';

import { IoCameraOutline } from 'react-icons/io5';

import type { BaseComponentProps } from '../../../../base-component.types';
import { IconButton } from '../../../../icon-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../tooltip';
import {
  AnswerSheetGuideDialog,
  prefetchExample,
} from './AnswerSheetGuideDialog';

import styles from './AnswerSheetUpload.module.scss';

/**
 * `.heic` and `.heif` are spelled out because `image/*` only covers the types the
 * browser has a mapping for, and one it does not know is greyed out in the picker —
 * unselectable rather than merely unread. iPhones shoot HEIC by default.
 */
const PHOTO_ACCEPT = 'image/*,.heic,.heif';

type AnswerSheetUploadProps = BaseComponentProps & {
  disabled?: boolean;
  loading?: boolean;
  onSelect: (photo: File) => void;
};

/** Takes a photo of the paper answer sheet, or picks one already taken. */
function AnswerSheetUpload({
  style,
  className,
  disabled = false,
  loading = false,
  onSelect,
  testId = 'ui-answer-sheet-upload',
}: AnswerSheetUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    event.target.value = '';

    // Not filtered on the browser's word for what this is. It is wrong often enough
    // to matter — `''` for an extension it has no mapping for, `application/octet-stream`
    // for the .heic an iPhone shoots — and every file turned away here was turned away
    // silently, which is exactly the "picking a photo does nothing" this was meant to
    // fix. The read that follows resolves the type off the extension and reports what
    // it cannot handle, so it is the only gate that needs to exist.
    if (file) {
      onSelect(file);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        className={styles['input']}
        type="file"
        accept={PHOTO_ACCEPT}
        tabIndex={-1}
        aria-hidden="true"
        data-testid={`${testId}_input`}
        onChange={handleChange}
      />
      <Tooltip placement="top" mouseOnly={true}>
        <TooltipTrigger asChild={true}>
          <IconButton
            style={style}
            className={className}
            icon={<IoCameraOutline />}
            disabled={disabled}
            loading={loading}
            aria-label="Subir foto de la plantilla de respuestas"
            onPointerEnter={prefetchExample}
            onFocus={prefetchExample}
            onClick={() => setIsGuideOpen(true)}
            testId={testId}
          />
        </TooltipTrigger>
        <TooltipContent className={styles['tooltip']}>
          Sube una foto de la plantilla para leer tus respuestas
        </TooltipContent>
      </Tooltip>
      <AnswerSheetGuideDialog
        open={isGuideOpen}
        testId={`${testId}_guide`}
        onConfirm={() => {
          setIsGuideOpen(false);
          inputRef.current?.click();
        }}
        onClose={() => setIsGuideOpen(false)}
      />
    </>
  );
}

export { AnswerSheetUpload };
